import React, { useState } from 'react';
import { Button } from '../common/Button';
import toast from 'react-hot-toast';
import { submitFeedback } from '../../services/feedbackService';
import { useAuth } from '../../context/AuthContext';

const BugIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 2l1.88 1.88" /><path d="M14.12 3.88L16 2" />
    <path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1" />
    <path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6" />
    <path d="M12 20v-9" /><path d="M6.53 9C4.6 8.8 3 7.1 3 5" />
    <path d="M6 13H2" /><path d="M3 21c0-2.1 1.7-3.9 3.8-4" />
    <path d="M20.97 5c0 2.1-1.6 3.8-3.5 4" /><path d="M22 13h-4" />
    <path d="M17.2 17c2.1.1 3.8 1.9 3.8 4" />
  </svg>
);

const SparkleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
  </svg>
);

const LightbulbIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
    <path d="M9 18h6" /><path d="M10 22h4" />
  </svg>
);

const FEEDBACK_TYPES = [
  { value: 'bug', label: 'Bug Report', icon: <BugIcon /> },
  { value: 'feature_request', label: 'Feature Request', icon: <SparkleIcon /> },
  { value: 'improvement', label: 'Improvement', icon: <LightbulbIcon /> },
];

const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Low', description: 'Minor issue, workaround exists' },
  { value: 'medium', label: 'Medium', description: 'Affects usability but not blocking' },
  { value: 'high', label: 'High', description: 'Blocking or data loss' },
];

const PLACEHOLDER_MAP = {
  bug: 'What happened? What did you expect to happen?',
  feature_request: 'Describe the feature you\'d like to see...',
  improvement: 'What could we do better?',
};

const SUBMIT_LABEL = {
  bug: 'Submit Bug Report',
  feature_request: 'Submit Feature Request',
  improvement: 'Submit Improvement',
};

function StarRating({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star === value ? null : star)}
          className={`text-2xl transition-colors ${
            star <= (value || 0) ? 'text-yellow-400' : 'text-gray-600 hover:text-gray-400'
          }`}
          aria-label={`Rate ${star} out of 5`}
        >
          {star <= (value || 0) ? '★' : '☆'}
        </button>
      ))}
    </div>
  );
}

export default function FeedbackForm({ onSuccess }) {
  const { user, betaStatus } = useAuth();
  const [type, setType] = useState('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [stepsToReproduce, setStepsToReproduce] = useState('');
  const [csat, setCsat] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) {
      toast.error('Title and description are required');
      return;
    }

    setSubmitting(true);
    try {
      await submitFeedback({
        type,
        title: title.trim(),
        description: description.trim(),
        severity: type === 'bug' ? severity : undefined,
        stepsToReproduce: type === 'bug' ? stepsToReproduce.trim() : undefined,
        csat,
        user,
        betaStatus,
      });
      toast.success('Submitted! A GitHub issue will be created automatically.');
      onSuccess?.();
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Type selector */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">Type</label>
        <div className="flex gap-2">
          {FEEDBACK_TYPES.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setType(opt.value)}
              className={`px-3 py-1.5 rounded-md border text-sm transition-colors ${
                type === opt.value
                  ? 'border-purple-500 bg-purple-900/30 text-purple-300'
                  : 'border-gray-600 bg-gray-700 text-gray-400 hover:border-gray-500'
              }`}
            >
              <span className="inline-flex items-center gap-1.5"><span className="inline-flex">{opt.icon}</span>{opt.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Title <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Brief summary"
          className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
          disabled={submitting}
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Description <span className="text-red-400">*</span>
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={PLACEHOLDER_MAP[type]}
          rows={4}
          className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
          disabled={submitting}
        />
      </div>

      {/* Bug-only fields */}
      {type === 'bug' && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Severity</label>
            <div className="flex gap-3">
              {SEVERITY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSeverity(opt.value)}
                  className={`flex-1 p-2 rounded-md border text-sm transition-colors ${
                    severity === opt.value
                      ? 'border-red-500 bg-red-900/30 text-red-300'
                      : 'border-gray-600 bg-gray-700 text-gray-400 hover:border-gray-500'
                  }`}
                >
                  <div className="font-medium">{opt.label}</div>
                  <div className="text-xs mt-0.5 opacity-70">{opt.description}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Steps to Reproduce <span className="text-gray-500">(optional)</span>
            </label>
            <textarea
              value={stepsToReproduce}
              onChange={(e) => setStepsToReproduce(e.target.value)}
              placeholder={"1. Go to...\n2. Click on...\n3. See error..."}
              rows={3}
              className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
              disabled={submitting}
            />
          </div>
        </>
      )}

      {/* CSAT */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          How satisfied are you with Chaggerheart? <span className="text-gray-500">(optional)</span>
        </label>
        <StarRating value={csat} onChange={setCsat} />
      </div>

      {/* Auto-metadata note */}
      <div className="text-xs text-gray-500">
        Your device info, current page, and account details will be attached automatically.
      </div>

      <Button
        type="submit"
        variant="primary"
        disabled={submitting || !title.trim() || !description.trim()}
        className="w-full"
      >
        {submitting ? 'Submitting...' : SUBMIT_LABEL[type]}
      </Button>
    </form>
  );
}
